import { useMemo, useState } from 'react';
import {
  useGetGeneralSummaryQuery,
  useGetGeneralMetricsQuery,
  type GroupByPeriod,
} from '@/entities/Dashboard';
import { Svg } from '@/shared/ui/Svg';
import NoteIcon from '@/shared/assets/icons/dashboard/Note.svg';
import UsersIcon from '@/shared/assets/icons/dashboard/Users.svg';
import { buildMetric } from '../../../lib/metrics';
import { toAttendanceChartPoints } from '../../../lib/attendance';
import {
  DASHBOARD_DATE_PERIOD_OPTIONS,
  toDashboardDateParam,
  type DashboardDatePeriod,
} from '../../../lib/datePeriod';
import type { MetricItem } from '../../../config/mockDashboardData';
import { AnalyticsMetricsGrid } from '../../components/AnalyticsMetricCard';
import { AnalyticsSectionHeader } from '../../components/AnalyticsSectionHeader';
import { AttendanceEngagementChart } from '../../components/AttendanceEngagementChart';
import styles from '../../dashboard/Dashboard.module.scss';

const GROUP_BY_OPTIONS: { key: GroupByPeriod; label: string }[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

interface GeneralInfoTabProps {
  onExport: () => void;
}

export const GeneralInfoTab = ({ onExport }: GeneralInfoTabProps) => {
  const [datePeriod, setDatePeriod] = useState<DashboardDatePeriod>('this-month');
  const [groupBy, setGroupBy] = useState<GroupByPeriod>('month');
  const date = toDashboardDateParam(datePeriod);

  const { data: summary } = useGetGeneralSummaryQuery({ date });
  const { data: metricsData } = useGetGeneralMetricsQuery({ groupBy });

  const metrics = useMemo<MetricItem[]>(() => {
    const s = summary?.data;
    return [
      buildMetric('1', 'Total Students', s?.totalStudents, 'count'),
      buildMetric('2', 'Active Students', s?.activeStudents, 'count'),
      buildMetric('3', 'New Registrations', s?.newRegistrations, 'count'),
      buildMetric('4', 'Courses Completion Rate', s?.courseCompletionRate, 'percent'),
      buildMetric('5', 'Dropout Rate', s?.dropoutRate, 'percent'),
      buildMetric('6', 'Course Diploma Rate', s?.courseDiplomaRate, 'percent'),
    ];
  }, [summary]);

  const chartData = useMemo(() => toAttendanceChartPoints(metricsData?.data), [metricsData]);

  return (
    <div className={styles.root}>
      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="General Info"
          icon={<Svg Icon={NoteIcon} width={20} height={20} className="text-[#0068D6]" />}
          iconBg="#E8F0FF"
          onExport={onExport}
          periodOptions={DASHBOARD_DATE_PERIOD_OPTIONS}
          periodValue={datePeriod}
          onPeriodChange={(key) => setDatePeriod(key as DashboardDatePeriod)}
        />
        <AnalyticsMetricsGrid metrics={metrics} columns={6} />
      </section>

      <section className={styles.section}>
        <AnalyticsSectionHeader
          title="Attendance & Engagement Trends"
          icon={<Svg Icon={UsersIcon} width={20} height={20} className="text-[#1E7E34]" />}
          iconBg="#E8F8EE"
          onExport={onExport}
          periodOptions={GROUP_BY_OPTIONS}
          periodValue={groupBy}
          onPeriodChange={(key) => setGroupBy(key as GroupByPeriod)}
        />
        <AttendanceEngagementChart data={chartData} />
      </section>
    </div>
  );
};
